import { apiClient } from "./client";
import type {
  ReferenceTable,
  ReferenceTableCreate,
  ReferenceTableUpdate,
  ReferenceTablesPaginated,
  ReferenceTableImport,
  SimulatorVersionReferenceTable,
  VersionReferenceTable,
  MessageResponse,
} from "@/types";

interface ListReferencesParams {
  page?: number;
  page_size?: number;
  search?: string;
  simulator_ids?: string;
}

export interface SimulatorUsageInfo {
  id: number;
  name: string;
  slug: string;
  reference_table_count: number;
  reference_table_ids: number[];
}

export interface ReferenceTablesUsageBySimulator {
  simulators: SimulatorUsageInfo[];
}

export async function listReferenceTables(
  params: ListReferencesParams = {}
): Promise<ReferenceTablesPaginated> {
  const response = await apiClient.get<ReferenceTablesPaginated>(
    `/references`,
    { params }
  );
  return response.data;
}

/**
 * Toutes les grilles, page après page. La liste est plafonnée à 100 grilles par page :
 * un seul appel en perdrait silencieusement au-delà.
 */
export async function listAllReferenceTables(
  params: Omit<ListReferencesParams, "page" | "page_size"> = {}
): Promise<ReferenceTablesPaginated> {
  const first = await listReferenceTables({ ...params, page: 1, page_size: 100 });
  const items = [...first.items];
  for (let page = 2; page <= first.total_pages; page++) {
    const next = await listReferenceTables({ ...params, page, page_size: 100 });
    items.push(...next.items);
  }
  return { ...first, items, total_pages: 1, current_page: 1, page_size: items.length };
}

export async function getReferenceTable(
  referenceId: number
): Promise<ReferenceTable> {
  const response = await apiClient.get<ReferenceTable>(
    `/references/${referenceId}`
  );
  return response.data;
}

export async function getReferenceTableBySlug(
  slug: string
): Promise<ReferenceTable> {
  const response = await apiClient.get<ReferenceTable>(
    `/references/by-slug/${slug}`
  );
  return response.data;
}

export async function createReferenceTable(
  data: ReferenceTableCreate
): Promise<ReferenceTable> {
  const response = await apiClient.post<ReferenceTable>(
    `/references`,
    data
  );
  return response.data;
}

export async function updateReferenceTable(
  referenceId: number,
  data: ReferenceTableUpdate
): Promise<ReferenceTable> {
  const response = await apiClient.patch<ReferenceTable>(
    `/references/${referenceId}`,
    data
  );
  return response.data;
}

export interface NullifiedField {
  field_id: number;
  field_slug: string;
  field_label: string;
  version_id: number;
  table_name?: string;
  table_slug?: string;
}

export interface NullifiedReferences {
  count: number;
  table_slug?: string;
  fields: NullifiedField[];
  warning: string;
}

export interface DeleteReferenceTableResult {
  message: string;
  nullified_references?: NullifiedReferences;
}

export async function deleteReferenceTable(
  referenceId: number
): Promise<DeleteReferenceTableResult> {
  const response = await apiClient.delete<DeleteReferenceTableResult>(
    `/references/${referenceId}`
  );
  return response.data;
}

export async function duplicateReferenceTable(
  referenceId: number
): Promise<ReferenceTable> {
  const response = await apiClient.post<ReferenceTable>(
    `/references/${referenceId}/duplicate`
  );
  return response.data;
}

export async function importReferenceData(
  referenceId: number,
  data: ReferenceTableImport
): Promise<ReferenceTable> {
  const response = await apiClient.post<ReferenceTable>(
    `/references/${referenceId}/import`,
    data
  );
  return response.data;
}

export async function getReferenceTablesUsageBySimulator(): Promise<ReferenceTablesUsageBySimulator> {
  const response = await apiClient.get<ReferenceTablesUsageBySimulator>(
    `/references/usage-by-simulator`
  );
  return response.data;
}

export interface BulkDeleteResult {
  message: string;
  deleted_count: number;
  errors?: string[];
  nullified_references?: NullifiedReferences;
}

export async function bulkDeleteReferenceTables(
  ids: number[]
): Promise<BulkDeleteResult> {
  const response = await apiClient.post<BulkDeleteResult>(
    `/references/bulk-delete`,
    ids
  );
  return response.data;
}

// ============== Usage Statistics Types ==============

export interface ReferenceTableUsageCounts {
  usage_counts: Record<number, { input_fields: number; output_fields: number }>;
}

export interface ReferenceTableInputFieldUsage {
  id: number;
  slug: string;
  label: string;
  field_type: string | null;
  version_id: number;
  version_name: string | null;
  simulator_id: number | null;
  simulator_name: string | null;
  simulator_slug: string | null;
  reference_column: string | null;
  reference_value_column: string | null;
  reference_filter: Record<string, unknown> | null;
  usage_type: "direct" | "conditional";
}

export interface ReferenceTableOutputFieldUsage {
  id: number;
  slug: string;
  label: string;
  version_id: number | null;
  version_name: string | null;
  simulator_id: number | null;
  simulator_name: string | null;
  simulator_slug: string | null;
  formula_excerpt: string;
  static_filters: Record<string, string[]>;
  source_type: "output_field" | "variable";
}

export interface FieldReference {
  id: number;
  slug: string;
  label: string;
  simulator_name: string | null;
  simulator_slug: string | null;
}

export interface ValueUsage {
  column: string;
  value: string;
  input_fields: FieldReference[];
  output_fields: FieldReference[];
}

export interface ReferenceTableUsageDetail {
  table_id: number;
  table_slug: string;
  table_name: string;
  total_input_fields: number;
  total_output_fields: number;
  input_fields: ReferenceTableInputFieldUsage[];
  output_fields: ReferenceTableOutputFieldUsage[];
  value_usage: ValueUsage[];
}

// ============== Usage Statistics API Functions ==============

export async function getReferenceTablesUsageCounts(): Promise<ReferenceTableUsageCounts> {
  const response = await apiClient.get<ReferenceTableUsageCounts>(
    `/references/usage-counts`
  );
  return response.data;
}

export async function getReferenceTableUsage(
  referenceId: number
): Promise<ReferenceTableUsageDetail> {
  const response = await apiClient.get<ReferenceTableUsageDetail>(
    `/references/${referenceId}/usage`
  );
  return response.data;
}

// ============== Version Reference Table Links ==============

export async function listVersionReferenceTables(
  versionId: number
): Promise<VersionReferenceTable[]> {
  const response = await apiClient.get<VersionReferenceTable[]>(
    `/references/versions/${versionId}/reference-tables`
  );
  return response.data;
}

/**
 * Grilles vues par une version pendant une simulation : celles qui lui sont liées, ou toutes
 * si elle n'en lie aucune — la même règle que le moteur de calcul.
 */
export async function listReferenceTablesForVersion(
  versionId: number
): Promise<VersionReferenceTable[]> {
  const linked = await listVersionReferenceTables(versionId);
  if (linked.length > 0) return linked;
  return (await listAllReferenceTables()).items;
}

/**
 * Grille désignée par une liste déroulante. La liste peut viser un autre millésime d'une grille
 * liée (même slug, autre id) : c'est la grille liée à la version qui fait foi, comme pour le moteur.
 */
export function findReferenceTable(
  tables: VersionReferenceTable[] | undefined,
  id: number | null | undefined
): VersionReferenceTable | undefined {
  if (!tables || id == null) return undefined;
  return tables.find((t) => t.id === id) ?? tables.find((t) => t.equivalent_ids?.includes(id));
}

export async function linkReferenceTableToVersion(
  versionId: number,
  referenceTableId: number
): Promise<SimulatorVersionReferenceTable> {
  const response = await apiClient.post<SimulatorVersionReferenceTable>(
    `/references/versions/${versionId}/reference-tables/${referenceTableId}`
  );
  return response.data;
}

export async function unlinkReferenceTableFromVersion(
  versionId: number,
  referenceTableId: number
): Promise<void> {
  await apiClient.delete(
    `/references/versions/${versionId}/reference-tables/${referenceTableId}`
  );
}
